/** 本地人像配方。关键点和手动区域均在原始图片的归一化坐标内保存。 */
export type RetouchPoint = { x: number; y: number; visibility?: number };
export const RETOUCH_GROUPS = {
  skin: [
    "smooth",
    "whiten",
    "even",
    "plump",
    "nasolabial",
    "darkCircles",
    "skinDetail",
    "skinTone",
  ],
  face: [
    "smoothFace",
    "smallFace",
    "slimFace",
    "narrowFace",
    "jawline",
    "jawbone",
    "cheekbone",
    "shortFace",
    "vFace",
    "chin",
    "lowerFace",
    "middleFace",
    "upperFace",
    "hairline",
  ],
  eyes: [
    "bigEyes",
    "brightEyes",
    "eyeDistance",
    "eyeCorners",
    "eyeHeight",
    "eyeTilt",
  ],
  nose: ["slimNose", "noseBridge", "noseHeight", "noseSize"],
  mouth: [
    "whiteTeeth",
    "mouthSize",
    "mouthHeight",
    "mouthTilt",
    "smileLips",
    "smile",
  ],
  brows: [
    "browHeight",
    "browDistance",
    "browPeak",
    "browTilt",
    "smoothBrow",
    "willowBrow",
    "swordBrow",
  ],
  makeup: [
    "lipstick",
    "blush",
    "contour",
    "eyeBags",
    "eyebrow",
    "eyelashes",
    "eyeliner",
    "eyeshadow",
    "contacts",
    "highlight",
    "freckles",
  ],
  body: [
    "smallHead",
    "swanNeck",
    "slimArms",
    "squareShoulders",
    "wideShoulders",
    "slimBody",
    "slimWaist",
    "longLegs",
    "bust",
    "hips",
    "bodySmooth",
    "bodyWhiten",
  ],
} as const;
export type RetouchGroup = keyof typeof RETOUCH_GROUPS;
export type RetouchParameter = (typeof RETOUCH_GROUPS)[RetouchGroup][number];
export const RETOUCH_SIGNED = new Set<RetouchParameter>([
  "chin",
  "lowerFace",
  "middleFace",
  "upperFace",
  "hairline",
  "eyeDistance",
  "eyeHeight",
  "eyeTilt",
  "noseBridge",
  "noseHeight",
  "noseSize",
  "mouthSize",
  "mouthHeight",
  "mouthTilt",
  "browHeight",
  "browDistance",
  "browPeak",
  "browTilt",
  "skinTone",
  "wideShoulders",
  "bust",
  "hips",
]);
export type ManualRetouch = {
  id: string;
  mode: "stretch" | "slim" | "size";
  x: number;
  y: number;
  width: number;
  height: number;
  amount: number;
};
export interface ImageRetouch {
  enabled?: boolean;
  values?: Partial<Record<RetouchParameter, number>>;
  groups?: Partial<Record<RetouchGroup, boolean>>;
  faceIndex?: number;
  bodyIndex?: number;
  analysis?: {
    sourceKey: string;
    faces: RetouchPoint[][];
    bodies: RetouchPoint[][];
  } | null;
  manual?: ManualRetouch[];
  makeupColor?: string;
}
export interface NormalizedImageRetouch {
  enabled: boolean;
  values: Record<RetouchParameter, number>;
  groups: Record<RetouchGroup, boolean>;
  faceIndex: number;
  bodyIndex: number;
  analysis: NonNullable<ImageRetouch["analysis"]> | null;
  manual: ManualRetouch[];
  makeupColor: string;
}
const clamp = (n: number | undefined, min: number, max: number, initial = 0) =>
  Number.isFinite(n) ? Math.max(min, Math.min(max, n!)) : initial;
export function normalizeImageRetouch(
  r?: ImageRetouch | null,
): NormalizedImageRetouch {
  const points = (groups?: RetouchPoint[][], count = 478) =>
    (Array.isArray(groups) ? groups : [])
      .slice(0, 4)
      .filter(
        (p) =>
          Array.isArray(p) &&
          p.length >= count &&
          p.every((q) => q && Number.isFinite(q.x) && Number.isFinite(q.y)),
      )
      .map((p) =>
        p.slice(0, count).map((q) => ({
          x: clamp(q.x, -1, 2),
          y: clamp(q.y, -1, 2),
          visibility: clamp(q.visibility, 0, 1, 1),
        })),
      );
  return {
    enabled: r?.enabled !== false,
    values: Object.fromEntries(
      Object.values(RETOUCH_GROUPS)
        .flat()
        .map((key) => [
          key,
          clamp(r?.values?.[key], RETOUCH_SIGNED.has(key) ? -100 : 0, 100),
        ]),
    ) as NormalizedImageRetouch["values"],
    groups: Object.fromEntries(
      Object.keys(RETOUCH_GROUPS).map((key) => [
        key,
        r?.groups?.[key as RetouchGroup] !== false,
      ]),
    ) as NormalizedImageRetouch["groups"],
    faceIndex: Math.floor(clamp(r?.faceIndex, -1, 3, -1)),
    bodyIndex: Math.floor(clamp(r?.bodyIndex, -1, 3, -1)),
    analysis: r?.analysis
      ? {
          sourceKey: String(r.analysis.sourceKey),
          faces: points(r.analysis.faces),
          bodies: points(r.analysis.bodies, 33),
        }
      : null,
    manual: (Array.isArray(r?.manual) ? r.manual : [])
      .filter((m) => m && typeof m === "object")
      .slice(0, 32)
      .map((m, i) => ({
        id: String(m.id ?? i),
        mode: ["stretch", "slim", "size"].includes(m.mode) ? m.mode : "size",
        x: clamp(m.x, 0, 1, 0.5),
        y: clamp(m.y, 0, 1, 0.5),
        width: clamp(m.width, 0.02, 1, 0.4),
        height: clamp(m.height, 0.02, 1, 0.4),
        amount: clamp(m.amount, -100, 100),
      })),
    makeupColor: /^#[\da-f]{6}$/i.test(r?.makeupColor ?? "")
      ? r!.makeupColor!
      : "#b83c55",
  };
}
export function hasImageRetouch(input?: ImageRetouch | null) {
  const r = normalizeImageRetouch(input);
  return (
    r.enabled &&
    (r.manual.some((m) => m.amount !== 0) ||
      Object.entries(RETOUCH_GROUPS).some(
        ([group, keys]) =>
          r.groups[group as RetouchGroup] &&
          keys.some(
            (key) => r.values[key] !== 0 && retouchParameterAvailable(r, key),
          ),
      ))
  );
}
export function getImageRetouchSignature(input?: ImageRetouch | null) {
  return JSON.stringify(normalizeImageRetouch(input));
}
export function retouchSourceKey(source: string) {
  let hash = 2166136261;
  for (let i = 0; i < source.length; i++)
    hash = Math.imul(hash ^ source.charCodeAt(i), 16777619);
  return (hash >>> 0).toString(36);
}
export const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378,
  400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21,
  54, 103, 67, 109,
];
export const FACE_LEFT_EYE = [
  33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246,
];
export const FACE_RIGHT_EYE = [
  263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388,
  466,
];
export const FACE_LIPS = [
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37,
  39, 40, 185,
];
export const FACE_INNER_LIPS = [
  78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310, 311, 312, 13, 82,
  81, 80, 191,
];
export const FACE_LEFT_BROW = [70, 63, 105, 66, 107, 55, 65, 52, 53, 46];
export const FACE_RIGHT_BROW = [
  336, 296, 334, 293, 300, 276, 283, 282, 295, 285,
];
export type RetouchWarp = {
  x: number;
  y: number;
  rx: number;
  ry: number;
  sx: number;
  sy: number;
  dx: number;
  dy: number;
  angle: number;
  rotation: number;
};
/** 紧支撑椭圆逆向形变；所有输出像素仅采样原图一次，避免多次插值糊边。 */
export function mapRetouchPoint(
  point: RetouchPoint,
  warps: RetouchWarp[],
  aspect = 1,
): RetouchPoint {
  let x = point.x * aspect,
    y = point.y;
  for (let i = warps.length - 1; i >= 0; i--) {
    const w = warps[i],
      c = Math.cos(w.rotation),
      s = Math.sin(w.rotation),
      xx = c * (x - w.x) + s * (y - w.y),
      yy = -s * (x - w.x) + c * (y - w.y),
      d = Math.hypot(xx / w.rx, yy / w.ry);
    if (d >= 1) continue;
    const t = 1 - d,
      weight = t * t * (3 - 2 * t),
      a = -w.angle * weight,
      ca = Math.cos(a),
      sa = Math.sin(a),
      u = (xx - w.dx * weight) / (1 + w.sx * weight),
      v = (yy - w.dy * weight) / (1 + w.sy * weight),
      rx = ca * u - sa * v,
      ry = sa * u + ca * v;
    x = w.x + c * rx - s * ry;
    y = w.y + s * rx + c * ry;
  }
  return { x: x / aspect, y };
}
export function selectedRetouchFaces(r: NormalizedImageRetouch) {
  const all = r.analysis?.faces ?? [];
  return r.faceIndex < 0 ? all : all.slice(r.faceIndex, r.faceIndex + 1);
}
export function selectedRetouchBodies(r: NormalizedImageRetouch) {
  const all = r.analysis?.bodies ?? [];
  return r.bodyIndex < 0 ? all : all.slice(r.bodyIndex, r.bodyIndex + 1);
}
/** 不把模型推断出的画外关节当作可编辑部位。 */
export function retouchPointsVisible(p: RetouchPoint[], indices: number[]) {
  return indices.every(
    (i) =>
      p[i] &&
      (p[i].visibility ?? 1) > 0.5 &&
      p[i].x >= 0 &&
      p[i].x <= 1 &&
      p[i].y >= 0 &&
      p[i].y <= 1,
  );
}
export function bodyParameterAvailable(
  p: RetouchPoint[],
  key: RetouchParameter,
) {
  const visible = (...indices: number[]) => retouchPointsVisible(p, indices);
  if (key === "slimArms") return visible(11, 13) || visible(12, 14);
  if (key === "longLegs")
    return visible(11, 12, 23, 27) || visible(11, 12, 24, 28);
  if (key === "smallHead" || key === "swanNeck") return visible(0, 11, 12);
  if (key === "squareShoulders" || key === "wideShoulders")
    return visible(11, 12);
  if (key === "bodySmooth" || key === "bodyWhiten")
    return (
      visible(11, 12, 23, 24) ||
      visible(11, 13) ||
      visible(12, 14) ||
      visible(23, 25) ||
      visible(24, 26)
    );
  return visible(11, 12, 23, 24);
}
export function retouchParameterAvailable(
  r: NormalizedImageRetouch,
  key: RetouchParameter,
) {
  return (RETOUCH_GROUPS.body as readonly string[]).includes(key)
    ? selectedRetouchBodies(r).some((p) => bodyParameterAvailable(p, key))
    : selectedRetouchFaces(r).length > 0;
}
export function buildRetouchWarps(
  input?: ImageRetouch | null,
  aspect = 1,
): RetouchWarp[] {
  const r = normalizeImageRetouch(input),
    warps: RetouchWarp[] = [];
  if (!r.enabled) return warps;
  const values = { ...r.values };
  for (const [group, keys] of Object.entries(RETOUCH_GROUPS))
    if (!r.groups[group as RetouchGroup])
      for (const key of keys) values[key] = 0;
  for (const raw of selectedRetouchFaces(r)) {
    const p = raw.map((q) => ({ ...q, x: q.x * aspect }));
    // 人脸局部坐标跟随眼线；侧脸仍由各点位置决定，不假定正对镜头。
    const eye1 = p[33],
      eye2 = p[263],
      rotation = Math.atan2(eye2.y - eye1.y, eye2.x - eye1.x),
      fw = Math.hypot(p[234].x - p[454].x, p[234].y - p[454].y),
      fh = Math.hypot(p[10].x - p[152].x, p[10].y - p[152].y);
    const add = (
      key: RetouchParameter,
      index: number,
      rx: number,
      ry: number,
      sx = 0,
      sy = 0,
      dx = 0,
      dy = 0,
      angle = 0,
    ) => {
      const a = values[key] / 100;
      if (!a) return;
      const q = p[index];
      warps.push({
        x: q.x,
        y: q.y,
        rx: Math.max(0.003, rx * fw),
        ry: Math.max(0.003, ry * fh),
        sx: sx * a,
        sy: sy * a,
        dx: dx * fw * a,
        dy: dy * fh * a,
        angle: angle * a,
        rotation,
      });
    };
    add("smoothFace", 168, 0.75, 0.75, -0.05, -0.02);
    add("smallFace", 168, 0.85, 0.9, -0.15, -0.15);
    add("slimFace", 4, 0.8, 0.8, -0.2, 0);
    add("narrowFace", 168, 0.85, 0.75, -0.17, 0);
    for (const [idx, sign] of [
      [172, 1],
      [397, -1],
    ] as const) {
      add("jawline", idx, 0.38, 0.32, 0, 0, 0.045 * sign, -0.025);
      add("jawbone", idx, 0.35, 0.35, 0, 0, 0.07 * sign, 0);
    }
    for (const [idx, sign] of [
      [234, 1],
      [454, -1],
    ] as const)
      add("cheekbone", idx, 0.3, 0.28, 0, 0, 0.06 * sign, 0);
    add("shortFace", 164, 0.7, 0.65, 0, -0.14);
    add("vFace", 152, 0.6, 0.45, -0.22, 0, 0, 0.018);
    add("chin", 152, 0.4, 0.27, 0, 0, 0, 0.07);
    add("lowerFace", 164, 0.65, 0.45, 0, 0.13);
    add("middleFace", 4, 0.65, 0.45, 0, 0.12);
    add("upperFace", 151, 0.65, 0.42, 0, 0.14);
    add("hairline", 10, 0.7, 0.28, 0, 0, 0, -0.07);
    for (const [center, sign, corner] of [
      [159, -1, 133],
      [386, 1, 362],
    ] as const) {
      add("bigEyes", center, 0.22, 0.13, 0.22, 0.3);
      add("eyeDistance", center, 0.3, 0.2, 0, 0, 0.055 * sign);
      add("eyeCorners", corner, 0.1, 0.1, 0.25, 0.05);
      add("eyeHeight", center, 0.3, 0.18, 0, 0, 0, -0.05);
      add("eyeTilt", center, 0.28, 0.17, 0, 0, 0, 0, 0.22 * sign);
    }
    add("slimNose", 4, 0.23, 0.25, -0.22, 0);
    add("noseBridge", 6, 0.2, 0.27, -0.16, 0);
    add("noseHeight", 4, 0.3, 0.3, 0, 0, 0, -0.05);
    add("noseSize", 4, 0.28, 0.26, 0.2, 0.2);
    add("mouthSize", 13, 0.35, 0.18, 0.22, 0.2);
    add("mouthHeight", 13, 0.4, 0.25, 0, 0, 0, -0.05);
    add("mouthTilt", 13, 0.4, 0.22, 0, 0, 0, 0, 0.22);
    for (const index of [61, 291]) {
      add("smileLips", index, 0.15, 0.12, 0, 0, 0, -0.025);
      add("smile", index, 0.22, 0.17, 0.05, 0, 0, -0.045);
    }
    for (const [idx, sign, peak] of [
      [105, -1, 66],
      [334, 1, 296],
    ] as const) {
      add("browHeight", idx, 0.3, 0.16, 0, 0, 0, -0.055);
      add("browDistance", idx, 0.3, 0.17, 0, 0, 0.05 * sign);
      add("browPeak", peak, 0.13, 0.1, 0, 0, 0, -0.035);
      add("browTilt", idx, 0.3, 0.16, 0, 0, 0, 0, 0.25 * sign);
      add("smoothBrow", idx, 0.3, 0.13, 0, -0.12);
      add("willowBrow", idx, 0.3, 0.14, 0.06, -0.15, 0, 0, 0.07 * sign);
      add("swordBrow", idx, 0.3, 0.14, 0.1, 0, 0, 0, -0.1 * sign);
    }
    // 局部丰盈以面颊形变实现，避免全脸简单放大。
    for (const idx of [205, 425]) add("plump", idx, 0.25, 0.2, 0.12, 0.1);
  }
  for (const raw of selectedRetouchBodies(r)) {
    const p = raw.map((q) => ({ ...q, x: q.x * aspect }));
    const l = p[11],
      rr = p[12],
      hip = { x: (p[23].x + p[24].x) / 2, y: (p[23].y + p[24].y) / 2 },
      shoulder = { x: (l.x + rr.x) / 2, y: (l.y + rr.y) / 2 },
      w = Math.hypot(l.x - rr.x, l.y - rr.y),
      h = retouchPointsVisible(raw, [11, 12, 23, 24])
        ? Math.hypot(hip.x - shoulder.x, hip.y - shoulder.y)
        : w * 1.3,
      rotation = Math.atan2(rr.y - l.y, rr.x - l.x);
    // 对齐人体左右方向，角度按无向肩线归一化。
    const rot =
      rotation > Math.PI / 2
        ? rotation - Math.PI
        : rotation < -Math.PI / 2
          ? rotation + Math.PI
          : rotation;
    const add = (
      key: RetouchParameter,
      pt: RetouchPoint,
      rx: number,
      ry: number,
      sx = 0,
      sy = 0,
      dy = 0,
    ) => {
      const a = values[key] / 100;
      if (!a || !bodyParameterAvailable(raw, key)) return;
      warps.push({
        x: pt.x,
        y: pt.y,
        rx: Math.max(0.01, w * rx),
        ry: Math.max(0.01, h * ry),
        sx: sx * a,
        sy: sy * a,
        dx: 0,
        dy: h * dy * a,
        angle: 0,
        rotation: rot,
      });
    };
    const mid = (a: RetouchPoint, b: RetouchPoint, t = 0.5) => ({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    });
    add("smallHead", p[0], 0.55, 0.6, -0.18, -0.18);
    add("swanNeck", mid(p[0], shoulder, 0.7), 0.4, 0.4, -0.08, 0.25);
    add("squareShoulders", shoulder, 1.1, 0.4, 0, -0.12, -0.025);
    add("wideShoulders", shoulder, 1.15, 0.5, 0.25, 0);
    for (const [a, b] of [
      [11, 13],
      [12, 14],
    ] as const)
      if (retouchPointsVisible(raw, [a, b]))
        add("slimArms", mid(p[a], p[b]), 0.25, 0.6, -0.25, 0);
    add("slimBody", mid(shoulder, hip), 0.85, 1.1, -0.2, 0);
    add("slimWaist", mid(shoulder, hip, 0.7), 0.7, 0.5, -0.25, 0);
    for (const [a, b] of [
      [23, 27],
      [24, 28],
    ] as const)
      if (retouchPointsVisible(raw, [a, b]))
        add("longLegs", mid(p[a], p[b]), 0.5, 1.8, 0, 0.15);
    add("bust", mid(shoulder, hip, 0.28), 0.7, 0.5, 0.16, 0.1);
    add("hips", hip, 0.8, 0.5, 0.2, 0.05);
  }
  for (const m of r.manual)
    if (m.amount)
      warps.push({
        x: m.x * aspect,
        y: m.y,
        rx: (m.width * aspect) / 2,
        ry: m.height / 2,
        sx:
          m.mode === "stretch"
            ? 0
            : (m.amount / 100) * (m.mode === "slim" ? -0.4 : 0.4),
        sy: m.mode === "slim" ? 0 : (m.amount / 100) * 0.4,
        dx: 0,
        dy: 0,
        angle: 0,
        rotation: 0,
      });
  return warps.slice(0, 384);
}

/** 来源变更时停用旧关键点；手动区域和用户参数保留，重新识别后继续可调。 */
export function retouchForSource(
  input: ImageRetouch | null | undefined,
  source: string,
): NormalizedImageRetouch {
  const r = normalizeImageRetouch(input);
  if (r.analysis && r.analysis.sourceKey !== retouchSourceKey(source))
    r.analysis = null;
  return r;
}

export type ImageRetouchPreset = Pick<
  NormalizedImageRetouch,
  "values" | "groups" | "makeupColor"
>;
/** 预设不包含人脸关键点、来源标识或手动选区。 */
export function createImageRetouchPreset(
  input: ImageRetouch,
): ImageRetouchPreset {
  const r = normalizeImageRetouch(input);
  return { values: r.values, groups: r.groups, makeupColor: r.makeupColor };
}
export const RETOUCH_BUILT_INS = [
  {
    id: "natural",
    group: "beauty",
    values: { smooth: 25, even: 15, whiten: 10, darkCircles: 12 },
  },
  {
    id: "beautySoft",
    group: "beauty",
    values: {
      smooth: 40,
      even: 22,
      whiten: 15,
      smallFace: 8,
      bigEyes: 8,
      brightEyes: 12,
    },
  },
  {
    id: "defined",
    group: "beauty",
    values: {
      skinDetail: 20,
      jawline: 18,
      cheekbone: 10,
      contour: 18,
      highlight: 12,
    },
  },
  {
    id: "bodyBalanced",
    group: "body",
    values: { slimBody: 12, slimWaist: 10, squareShoulders: 8 },
  },
  {
    id: "bodySlender",
    group: "body",
    values: {
      smallHead: 8,
      swanNeck: 12,
      slimArms: 12,
      slimBody: 18,
      slimWaist: 20,
      longLegs: 15,
    },
  },
] as const;
export function applyBuiltInRetouch(
  input: ImageRetouch,
  id: string,
): NormalizedImageRetouch {
  const r = normalizeImageRetouch(input),
    preset = RETOUCH_BUILT_INS.find((p) => p.id === id);
  if (!preset) return r;
  for (const [group, keys] of Object.entries(RETOUCH_GROUPS))
    if (preset.group === "body" ? group === "body" : group !== "body") {
      r.groups[group as RetouchGroup] = true;
      for (const key of keys) r.values[key] = 0;
    }
  Object.assign(r.values, preset.values);
  r.enabled = true;
  return r;
}
